import { createFileRoute, Link } from '@tanstack/react-router'
import { useCallback, useEffect, useRef, useState } from 'react'

import type { IntegrationSummary } from '@/api/admin-integrations-types'
import { useCreateTeyaConnectUrl, useDisconnectTeya } from '@/api/api-mutations'
import { useGetMe, useGetTeyaIntegration, useListIntegrations } from '@/api/react-query'
import { Badge } from '@/components/base/badge'
import { Button } from '@/components/base/button'
import { Container, ContainerHeader } from '@/components/base/container'
import { Heading } from '@/components/base/heading'
import { LoadingSpinner } from '@/components/base/loading-spinner'
import { usePrompt } from '@/components/base/prompt'
import { toast } from '@/components/base/toast'
import { SingleColumnPage } from '@/components/page-layout'
import { useI18n } from '@/i18n/i18n-react'
import { Puzzle } from '@/icons/puzzle'
import { isAdminOrDeveloperRole } from '@/util/invite-role-label'

export const Route = createFileRoute('/_authenticated/settings/integrations/')({
	component: IntegrationsPage
})

// Stop polling after this long so an abandoned OAuth tab doesn't keep the query refetching forever.
const CONNECT_POLL_TIMEOUT_MS = 3 * 60 * 1000
// How often to re-check the connection status while an OAuth tab is open.
const CONNECT_POLL_INTERVAL_MS = 2500

function IntegrationsPage() {
	const { L } = useI18n()
	const me = useGetMe()
	const integrations = useListIntegrations()
	// USER can view integrations but not change them; writes are ADMIN/DEVELOPER-gated on the backend too.
	const isAdmin = isAdminOrDeveloperRole(me.data?.role)

	// Teya keeps its bespoke OAuth card; every other provider renders a generic config card.
	const otherProviders = (integrations.data ?? []).filter(summary => summary.provider !== 'teya')

	return (
		<SingleColumnPage>
			<div className='space-y-6'>
				<div>
					<Heading level='h1'>{L.Settings.Integrations.Title()}</Heading>
					<p className='mt-2 text-sm text-ui-fg-subtle'>{L.Settings.Integrations.Description()}</p>
				</div>

				<TeyaIntegrationCard isAdmin={isAdmin} />

				{integrations.isPending ? (
					<LoadingSpinner />
				) : integrations.isError ? (
					<p className='txt-compact-small text-ui-fg-subtle'>{L.Error.SomethingWentWrong()}</p>
				) : (
					otherProviders.map(summary => <ProviderIntegrationCard key={summary.provider} summary={summary} />)
				)}
			</div>
		</SingleColumnPage>
	)
}

function ProviderIntegrationCard({ summary }: { summary: IntegrationSummary }) {
	const { L } = useI18n()
	const C = L.Settings.Integrations.Config
	const providers = L.Settings.Integrations.Providers
	const labels = summary.provider in providers ? providers[summary.provider] : undefined
	const name = labels?.Name() ?? summary.provider
	const description = labels?.Description() ?? ''

	return (
		<Container className='divide-y p-0'>
			<ContainerHeader
				heading={
					<div className='flex items-center gap-x-3'>
						<div className='flex size-10 items-center justify-center rounded-lg bg-ui-bg-base shadow-borders-base'>
							<Puzzle className='text-ui-fg-subtle' />
						</div>
						<div>
							<Heading level='h2'>{name}</Heading>
							<p className='txt-compact-small text-ui-fg-subtle'>{description}</p>
						</div>
					</div>
				}
				rightSide={
					summary.hasGlobalConfig ? (
						<Badge color='green'>{C.SecretSet()}</Badge>
					) : (
						<Badge color='grey'>{C.SecretUnset()}</Badge>
					)
				}
			/>

			<div className='flex items-center justify-between gap-3 px-3 py-3 sm:px-6 sm:py-4'>
				<div className='txt-compact-small text-ui-fg-subtle'>
					{summary.overrideCount > 0 ? C.OverrideCount({ count: summary.overrideCount }) : C.NoOverrides()}
				</div>
				<Link to='/settings/integrations/$provider' params={{ provider: summary.provider }}>
					<Button size='small' variant='secondary'>
						{C.Configure()}
					</Button>
				</Link>
			</div>
		</Container>
	)
}

function TeyaIntegrationCard({ isAdmin }: { isAdmin: boolean }) {
	const { L } = useI18n()
	const T = L.Settings.Integrations.Teya
	const prompt = usePrompt()

	// `connecting` is true from the moment we open the Teya tab until the backend reports ACTIVE
	// (or the timeout fires). It drives both the polling interval and the button/spinner state.
	const [connecting, setConnecting] = useState(false)
	const connectTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)

	const integration = useGetTeyaIntegration({
		refetchInterval: connecting ? CONNECT_POLL_INTERVAL_MS : false,
		refetchOnWindowFocus: true
	})

	const createConnectUrl = useCreateTeyaConnectUrl()
	const disconnect = useDisconnectTeya()

	const data = integration.data
	const available = data?.available ?? false
	const status = data?.status
	const isConnected = status === 'ACTIVE'

	const stopConnecting = useCallback(() => {
		setConnecting(false)
		if (connectTimeout.current) {
			clearTimeout(connectTimeout.current)
			connectTimeout.current = null
		}
	}, [])

	// Once the callback has exchanged the code the status flips to ACTIVE; end the polling loop.
	useEffect(() => {
		if (connecting && isConnected) {
			stopConnecting()
			toast.success(T.ConnectSuccess())
		}
	}, [connecting, isConnected, stopConnecting, T])

	// Clear the pending timeout if the user navigates away mid-connect.
	useEffect(() => stopConnecting, [stopConnecting])

	const handleConnect = useCallback(async () => {
		const result = await createConnectUrl({})
		if (!result.isSuccess) {
			toast.error(T.ConnectError())
			return
		}

		// The dashboard holds the admin JWT, so it fetches the authorize URL and opens it itself;
		// the tenant travels in the signed `state`. A blocked pop-up means we never left, so bail.
		const popup = window.open(result.response.authorizeUrl, '_blank', 'noopener,noreferrer')
		if (!popup) {
			toast.error(T.PopupBlocked())
			return
		}

		setConnecting(true)
		if (connectTimeout.current) clearTimeout(connectTimeout.current)
		connectTimeout.current = setTimeout(() => setConnecting(false), CONNECT_POLL_TIMEOUT_MS)
	}, [createConnectUrl, T])

	const handleDisconnect = useCallback(async () => {
		const ok = await prompt({
			title: L.General.AreYouSure(),
			description: T.DisconnectConfirm(),
			confirmText: T.Disconnect(),
			cancelText: L.General.Cancel(),
			variant: 'danger'
		})
		if (!ok) return

		stopConnecting()
		const result = await disconnect({})
		if (result.isSuccess) {
			toast.success(T.DisconnectSuccess())
		} else {
			toast.error(L.Error.SomethingWentWrong())
		}
	}, [prompt, L, T, disconnect, stopConnecting])

	return (
		<Container className='divide-y p-0'>
			<ContainerHeader
				heading={
					<div className='flex items-center gap-x-3'>
						<div className='flex size-10 items-center justify-center rounded-lg bg-ui-bg-base shadow-borders-base'>
							<Puzzle className='text-ui-fg-subtle' />
						</div>
						<div>
							<Heading level='h2'>{T.Name()}</Heading>
							<p className='txt-compact-small text-ui-fg-subtle'>{T.Description()}</p>
						</div>
					</div>
				}
				rightSide={<TeyaStatusBadge status={status} />}
			/>

			<div className='flex items-center justify-between gap-3 px-3 py-3 sm:px-6 sm:py-4'>
				<div className='txt-compact-small text-ui-fg-subtle'>
					<TeyaStatusDetail
						available={available}
						connecting={connecting}
						isPending={integration.isPending}
						isConnected={isConnected}
						connectedAt={data?.connectedAt}
						status={status}
						L={L}
					/>
				</div>

				{/* Connect/disconnect/reconnect and store-terminal config all mutate live routing, so they
				    are ADMIN-only; non-admins still see the status badge and detail above. */}
				{isAdmin && (
					<div className='flex shrink-0 items-center gap-x-2'>
						{isConnected && (
							<Link to='/settings/integrations/$provider' params={{ provider: 'teya' }}>
								<Button variant='secondary' size='small'>
									{T.ConfigureStoreTerminals()}
								</Button>
							</Link>
						)}
						{isConnected && (
							<Button variant='danger' size='small' onClick={() => void handleDisconnect()}>
								{T.Disconnect()}
							</Button>
						)}
						{available && (
							<Button
								variant={isConnected ? 'secondary' : 'primary'}
								size='small'
								onClick={() => void handleConnect()}
								isLoading={connecting}
								disabled={integration.isPending}
							>
								{isConnected || status === 'ERROR' ? T.Reconnect() : T.Connect()}
							</Button>
						)}
					</div>
				)}
			</div>
		</Container>
	)
}

function TeyaStatusDetail({
	available,
	connecting,
	isPending,
	isConnected,
	connectedAt,
	status,
	L
}: {
	available: boolean
	connecting: boolean
	isPending: boolean
	isConnected: boolean
	connectedAt?: string
	status?: 'NOT_CONNECTED' | 'ACTIVE' | 'ERROR' | 'REVOKED'
	L: ReturnType<typeof useI18n>['L']
}) {
	if (isPending) {
		return <LoadingSpinner />
	}
	if (connecting) {
		return <span>{L.Settings.Integrations.Teya.Connecting()}</span>
	}
	if (isConnected && connectedAt) {
		return (
			<span>
				{L.Settings.Integrations.Teya.ConnectedAt({ date: L.Date.ShortWithDate(new Date(connectedAt)) })}
			</span>
		)
	}
	if (status === 'ERROR') {
		return <span>{L.Settings.Integrations.Teya.ReconnectToConfigure()}</span>
	}
	// `available` only gates starting a new OAuth flow (env + platform publicId). An existing
	// connection is reported above; show Unavailable only when there is nothing to manage yet.
	if (!available) {
		return <span>{L.Settings.Integrations.Teya.Unavailable()}</span>
	}
	return null
}

function TeyaStatusBadge({ status }: { status?: 'NOT_CONNECTED' | 'ACTIVE' | 'ERROR' | 'REVOKED' }) {
	const { L } = useI18n()
	const T = L.Settings.Integrations.Teya

	if (status === 'ACTIVE') {
		return <Badge color='green'>{T.StatusConnected()}</Badge>
	}
	if (status === 'ERROR') {
		return <Badge color='orange'>{T.StatusError()}</Badge>
	}
	return <Badge color='grey'>{T.StatusDisconnected()}</Badge>
}
